/**
 * AdminSalesPace — "Regional Pace" card. Derives each region's pace
 * (trailing-6wk sold / cap) from the same revenueByRegion data, sorted
 * ascending so the regions needing attention sit at the top. Bar tone
 * steps from warn (<25%) → info (<40%) → primary.
 */
import type { AdminSalesDto } from './adminApi';

interface PaceRow {
  id: string;
  code: string;
  pct: number;
}

/** Two-letter region code from a slug (new-england → NE). */
function regionCode(slug: string): string {
  const initials = slug
    .split('-')
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');
  if (initials.length >= 2) return initials.slice(0, 2);
  return slug.slice(0, 2).toUpperCase();
}

/** Pace rows (sold/cap %), ascending. Regions with no capacity read 0%. */
export function toPaceRows(rows: AdminSalesDto['revenueByRegion']): PaceRow[] {
  return rows
    .map((r) => ({
      id: r.region.id,
      code: regionCode(r.region.slug),
      pct: r.ticketCap > 0 ? Math.round((r.ticketsSold / r.ticketCap) * 100) : 0,
    }))
    .sort((a, b) => a.pct - b.pct);
}

function paceTone(pct: number): string {
  if (pct < 25) return 'var(--nb-warn)';
  if (pct < 40) return 'var(--nb-info)';
  return 'var(--nb-primary)';
}

function Row({ row }: { row: PaceRow }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <span style={{ width: 50, fontSize: 11, fontWeight: 700, color: 'var(--nb-ink)' }}>
        {row.code}
      </span>
      <div
        style={{
          flex: 1,
          height: 6,
          background: 'var(--nb-surface-2)',
          borderRadius: 999,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            width: `${Math.min(100, row.pct)}%`,
            height: '100%',
            background: paceTone(row.pct),
          }}
        />
      </div>
      <span
        className="nb-tabular nb-mono"
        style={{ fontSize: 10.5, color: 'var(--nb-muted)', width: 32, textAlign: 'right' }}
      >
        {row.pct}%
      </span>
    </div>
  );
}

export function AdminSalesPace({ rows }: { rows: AdminSalesDto['revenueByRegion'] }) {
  const pace = toPaceRows(rows);
  return (
    <section className="nb-card" style={{ flex: 1, minHeight: 0 }}>
      <div className="nb-card-hd">
        <h3 className="nb-display" style={{ fontSize: 13, letterSpacing: '.04em', margin: 0 }}>
          Regional Pace
        </h3>
        <span className="nb-eyebrow">{pace.length} regions</span>
      </div>
      {pace.length === 0 ? (
        <div style={{ fontSize: 12, color: 'var(--nb-subtle)' }} role="note">
          Regional pace unavailable.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {pace.map((r) => (
            <Row key={r.id} row={r} />
          ))}
        </div>
      )}
    </section>
  );
}
